import React, { ChangeEvent, memo, useContext, useState } from 'react'
import { DatePicker } from 'components/forms/DatePicker'
import moment from 'moment-timezone'
import RemoveIcon from 'transfix-icons/build/RemoveIcon'
import { Button } from 'transfix-ui/components/Button'
import { DraggableListItem } from 'transfix-ui/components/DraggableList'
import SuggestLocation, { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { TimeInput, TIME_INPUT_FORMAT } from 'transfix-ui/components/TimeInput'
import { Box, Grid } from 'transfix-ui/core'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { FeatureFlagsContext } from 'utils/context/FeatureFlagsContext'
import { IAdditionalStops } from 'apps/marketplace/store/amQuoting/types'
import { FORMATS } from 'constants/datetime'

interface IAdditionalStopListItemProps {
  stop: IAdditionalStops
  index: number
  onChange: (change: RecursivePartial<IAdditionalStops>, index: number) => void
  onDeleteStop: (index: number) => void
}

const useDraggableListItemStyles = makeStyles(theme => ({
  bgColor: {
    background: brandColors.coolGray0,
    borderColor: brandColors.coolGray0,
    '& > div': {
      background: `${brandColors.coolGray0} !important`,
    },
  },
  boxSize: {
    '& > div': {
      '& > div': {
        padding: `${theme.spacing(2)} ${theme.spacing(0)} `,
      },
      '& > div:first-of-type': {
        padding: `${theme.spacing(0)} ${theme.spacing(1)} ${theme.spacing(0)} ${theme.spacing(0)}`,
        marginTop: theme.spacing(2),
      },
    },
  },
}))

const today = moment().startOf('day').toDate()

const inThreeMonths = moment().add(90, 'days').startOf('day').toDate()

export const AdditionalStopListItem = memo<IAdditionalStopListItemProps>(
  ({ index, onChange, onDeleteStop, stop }) => {
    const [location, setLocation] = useState<Omit<ILocationSuggestion, 'id'>>()

    const [startDate, setStartDate] = useState<string>()

    const [startTime, setStartTime] = useState<string>()

    const { getFlag } = useContext(FeatureFlagsContext)

    const { enabled: spotQuoteTool } = getFlag('spotQuoteTool')

    const classes = useDraggableListItemStyles()

    const locationHandler = (value: ILocationSuggestion) => {
      setLocation(value)
      onChange(
        {
          ...stop,
          startDate,
          city: value.city,
          state: value.state,
          countryCode: value.countryCode,
          postalCode: value.postalCode,
          startTime,
        },
        index
      )
    }

    const dateHandler = (value: string[]) => {
      const date = value[0] ? moment(value[0]).format(FORMATS.yearFirst) : ''
      setStartDate(date)
      onChange(
        {
          ...stop,
          ...location,
          startDate: date,
          startTime,
        },
        index
      )
    }

    const timeHandler = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setStartTime(value)
      onChange(
        {
          ...stop,
          ...location,
          startDate,
          startTime: value,
        },
        index
      )
    }

    const deleteStopHandler = (index: number) => {
      onDeleteStop(index)
    }

    return (
      <Box className={spotQuoteTool ? `${classes.bgColor} ${classes.boxSize}` : ''}>
        <DraggableListItem index={index} draggableId={stop.id.toString()} key={stop.id}>
          <Grid container direction='row' spacing={1}>
            <Grid item xs={12} md={12} lg={5}>
              <SuggestLocation
                label='Stop Location (Zip or City)'
                placeholder=''
                countries={['US']}
                resultTypes={['City', 'PostalCode']}
                value={location}
                onChange={locationHandler}
                id='am-quoting-pickup'
                dataTest='stop-location'
                large
                required
                clearable={location?.city !== undefined}
              />
            </Grid>
            <Grid item xs='auto' md={5} lg={3}>
              <label className='label' htmlFor='am-quoting-tool-pickup-date'>
                Stop Date
              </label>
              <DatePicker
                date={startDate || ''}
                placeholder='Optional'
                minDate={today}
                maxDate={inThreeMonths}
                onDateChange={dateHandler}
                id='am-quoting-tool-pickup-date'
                large
              />
            </Grid>
            <Grid item xs='auto' md={5} lg={4}>
              <Grid container direction='row'>
                <Grid item sm={9} lg={8}>
                  <TimeInput
                    label='Stop Time'
                    placeholder='Optional'
                    value={startTime || ''}
                    onChange={timeHandler}
                    id='spot-quote-additional-pickup-time'
                    dataTest='pickup-time'
                    fullWidth
                    large
                    timeFormat={TIME_INPUT_FORMAT.MILITARY}
                  />
                </Grid>
                <Grid item sm={3} lg={4}>
                  <Button
                    startIcon={<RemoveIcon size='large' />}
                    size='small'
                    color='tertiary'
                    className={`mt5 ${classes.bgColor}`}
                    onClick={() => deleteStopHandler(index)}
                    noPadding
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DraggableListItem>
      </Box>
    )
  }
)
